/**
 * Outils du HUD : menu de construction façon jeu de gestion (une rubrique à
 * la fois, cartes avec vignette 3D et prix, fiche détaillée au survol), barre
 * de placement, et statistiques de rendu. Les rubriques s'ouvrent depuis la
 * barre d'outils (dock.tsx) ; la fenêtre de direction est dans office.tsx.
 */
import { useState } from 'preact/hooks';
import { Activity, X } from 'lucide-preact';
import { BUILDINGS, LIFTS, liftCapacityPerHour, type BuildingKind, type BuildingSpec, type Difficulty, type LiftKind } from '@snowchain/shared';
import { Button, HudPanel } from './components';
import { PisteBuildBar, PisteBuildCard } from './pistes';
import { hud } from './context';
import { euros, n0 } from './format';
import { buildingIcon } from './labels';
import { Fact } from './game-ui';
import { buildCat, catMeta, liftIcon, thumbUrl } from './build-cats';
import type { AnyIcon } from './icons';
import { ui } from './ui-state';

const DIFFS: (Difficulty | 'auto')[] = ['auto', 'verte', 'bleue', 'rouge', 'noire'];
const DIFF_LABEL: Record<string, string> = { auto: 'Auto', verte: 'Verte', bleue: 'Bleue', rouge: 'Rouge', noire: 'Noire' };
const ZONE_LABEL: Record<BuildingSpec['zone'], string> = { village: 'Au village', domain: 'Sur le domaine', 'piste-side': 'En bord de piste', anywhere: 'Partout' };

interface BuildItem {
  key: string;
  label: string;
  cost: number;
  costText: string;
  icon: AnyIcon;
  desc: string;
  facts: [string, string][];
  pick: () => void;
}

function liftItem(k: LiftKind): BuildItem {
  const s = LIFTS[k];
  const cap = liftCapacityPerHour(k);
  return {
    key: `l:${k}`,
    label: s.label,
    cost: s.costBase,
    costText: `${euros(s.costBase, true)} + ${n0(s.costPerMeter)} €/m`,
    icon: liftIcon(k),
    desc: `${n0(cap)} personnes par heure. Posez la gare aval, puis la gare amont : la piste de descente est tracée avec la remontée.`,
    facts: [
      ['Débit', `${n0(cap)} pers./h`],
      ['Longueur', `${n0(s.minLength)} à ${n0(s.maxLength)} m`],
      ['Vent maximal', `${n0(s.windLimit)} km/h`],
      ['Chantier', `${n0(s.buildHours)} h`],
    ],
    pick: () => {
      const mode = ui.build.value;
      ui.build.value = { kind: 'lift', type: k, bottom: null, difficulty: mode?.kind === 'lift' ? mode.difficulty : 'auto' };
    },
  };
}

/** Capacités lisibles d'un bâtiment, tirées du catalogue. */
function buildingFacts(s: BuildingSpec): [string, string][] {
  const f: [string, string][] = [];
  if (s.parking) f.push(['Visiteurs accueillis', `+${n0(s.parking)}`]);
  if (s.ticketsPerHour) f.push(['Caisses', `${n0(s.ticketsPerHour)} forfaits/h`]);
  if (s.seats) f.push(['Restaurant', `${n0(s.seats)} places`]);
  if (s.rentalsPerDay) f.push(['Parc de skis', `${n0(s.rentalsPerDay)} paires`]);
  if (s.rentalsPerHour) f.push(['Comptoir de location', `${n0(s.rentalsPerHour)} clients/h`]);
  if (s.beds) f.push(['Lits', `${n0(s.beds)}${s.roomPrice ? ` · ${euros(s.roomPrice)} la nuit` : ''}`]);
  if (s.lessonsPerDay) f.push(['Cours', `${n0(s.lessonsPerDay)} par jour${s.lessonPrice ? ` · ${euros(s.lessonPrice)}` : ''}`]);
  if (s.waterPerHour) f.push(['Eau consommée', `${n0(s.waterPerHour)} m³/h`]);
  if (s.waterCapacity) f.push(["Retenue d'eau", `+${n0(s.waterCapacity)} m³`]);
  if (s.groomers) f.push(['Dameuses', n0(s.groomers)]);
  if (s.ambiance) f.push(['Ambiance', `+${s.ambiance.toLocaleString('fr-FR')} pt`]);
  f.push(['Entretien', `${euros(s.upkeepPerDay)}/j`]);
  f.push(['Chantier', `${n0(s.buildHours)} h`]);
  f.push(['Emplacement', ZONE_LABEL[s.zone] ?? s.zone]);
  return f;
}

function buildingItem(k: BuildingKind): BuildItem {
  const s = BUILDINGS[k];
  return {
    key: `b:${k}`,
    label: s.label,
    cost: s.cost,
    costText: euros(s.cost),
    icon: buildingIcon(k),
    desc: s.description,
    facts: buildingFacts(s),
    pick: () => {
      const mode = ui.build.value;
      ui.build.value = { kind: 'building', type: k, rot: mode?.kind === 'building' ? mode.rot : 0 };
    },
  };
}

function ItemCard(props: { item: BuildItem; cash: number; onHover: (key: string) => void }) {
  const i = props.item;
  const afford = props.cash >= i.cost;
  const thumb = thumbUrl(i.key);
  const Icon = i.icon;
  return (
    <button
      type="button"
      class={`g-item${afford ? '' : ' g-item-poor'}`}
      aria-disabled={!afford}
      onMouseEnter={() => props.onHover(i.key)}
      onFocus={() => props.onHover(i.key)}
      onClick={() => {
        if (afford) i.pick();
      }}
    >
      <span class="g-item-thumb">{thumb ? <img src={thumb} alt="" draggable={false} /> : <Icon size={44} />}</span>
      <span class="g-item-name">{i.label}</span>
      <span class="g-item-cost num">{i.costText}</span>
    </button>
  );
}

function ItemDetail(props: { item: BuildItem; cash: number }) {
  const i = props.item;
  const afford = props.cash >= i.cost;
  return (
    <aside class="g-build-detail">
      <div class="g-build-detail-name">{i.label}</div>
      <div class={`g-build-detail-cost num${afford ? '' : ' g-down'}`}>{i.costText}</div>
      <p class="g-build-detail-desc">{i.desc}</p>
      <div class="g-facts">
        {i.facts.map(([k, v]) => (
          <Fact key={k} label={k} value={v} />
        ))}
      </div>
      <p class={`g-build-detail-hint${afford ? '' : ' g-down'}`}>{afford ? 'Cliquez la carte, puis posez sur le terrain.' : `Il manque ${euros(i.cost - props.cash)}.`}</p>
    </aside>
  );
}

export function BuildMenu() {
  const [hover, setHover] = useState<string | null>(null);
  if (!ui.buildMenu.value) return null;
  void ui.tick.value;
  const mode = ui.build.value;
  const ghost = ui.ghost.value;
  const cash = hud.store?.replica?.me?.cash ?? 0;
  const closeAll = () => {
    ui.buildMenu.value = false;
    ui.build.value = null;
  };
  // Pendant le placement, le menu se replie en une barre : le terrain reste dégagé.
  if (mode?.kind === 'piste') return <PisteBuildBar />;
  if (mode) {
    const label = mode.kind === 'lift' ? LIFTS[mode.type].label : BUILDINGS[mode.type].label;
    const Icon = mode.kind === 'lift' ? liftIcon(mode.type) : buildingIcon(mode.type);
    const cat = catMeta(mode.kind === 'lift' ? 'lift' : BUILDINGS[mode.type].group);
    return (
      <HudPanel class="kh-build kh-build-compact" style={{ '--cat': cat.color }}>
        <div class="kh-hint" style={{ marginTop: 0 }}>
          <span class="kh-card-title">
            <span class="g-place-ic">
              <Icon size={18} />
            </span>
            {label}
          </span>
          {/* Pas de flex en ligne : la feuille de style met ce détail sur sa propre ligne (flex 1 1 100 %). */}
          <span style={{ minWidth: 0 }}>
            {ghost ? (
              ghost.ok ? (
                <span class="kh-valid">
                  {ghost.cost > 0 ? `${euros(ghost.cost)} \u00b7 ` : ''}
                  {ghost.detail ?? 'Emplacement valide'}
                </span>
              ) : (
                <span class="kh-invalid">{ghost.reason}</span>
              )
            ) : (
              <span class="muted">Survolez le terrain</span>
            )}
          </span>
          {mode.kind === 'lift' && (
            <span class="kh-seg" title="Difficulté visée pour la piste générée">
              {DIFFS.map((d) => (
                <button key={d} class={mode.difficulty === d ? 'on' : ''} onClick={() => (ui.build.value = { ...mode, difficulty: d })}>
                  {DIFF_LABEL[d]}
                </button>
              ))}
            </span>
          )}
          <span class="muted" style={{ whiteSpace: 'nowrap' }}>
            <kbd>Clic</kbd> {mode.kind === 'lift' ? (mode.bottom ? 'gare amont' : 'gare aval') : 'placer'} {mode.kind === 'building' && <><kbd>R</kbd> pivoter</>} <kbd>Échap</kbd> annuler
          </span>
          <Button size="sm" variant="secondary" onClick={() => (ui.build.value = null)}>
            Changer
          </Button>
        </div>
      </HudPanel>
    );
  }
  const cat = buildCat.value;
  const meta = catMeta(cat);
  const CatIcon = meta.icon;
  const items: BuildItem[] =
    cat === 'lift' ? (Object.keys(LIFTS) as LiftKind[]).map(liftItem) : cat === 'piste' ? [] : (Object.keys(BUILDINGS) as BuildingKind[]).filter((k) => BUILDINGS[k].group === cat).map(buildingItem);
  const shown = items.find((i) => i.key === hover) ?? items[0] ?? null;
  return (
    <section class="sc-hud kh-build g-build" style={{ '--cat': meta.color } as Record<string, string>} aria-label={`Construire : ${meta.label}`}>
      <div class="g-build-head">
        <span class="g-build-title">
          <CatIcon size={18} />
          {meta.label}
        </span>
        <span class="g-build-cash num">Trésorerie {euros(cash)}</span>
        <button class="sc-ibtn sc-ibtn-sm sc-ibtn-ghost" aria-label="Fermer" onClick={closeAll}>
          <X size={14} />
        </button>
      </div>
      <div class="g-build-body">
        <div class="g-build-items">
          {cat === 'piste' && (
            <>
              <PisteBuildCard />
              <button type="button" class="kh-card" onClick={() => (ui.pistesPanel.value = true)}>
                <span class="kh-card-title">Gestion des pistes</span>
                <span class="kh-card-desc">Liste des pistes, réseau du domaine et défauts à corriger (P).</span>
              </button>
            </>
          )}
          {items.map((i) => (
            <ItemCard key={i.key} item={i} cash={cash} onHover={setHover} />
          ))}
        </div>
        {shown ? (
          <ItemDetail item={shown} cash={cash} />
        ) : (
          <aside class="g-build-detail">
            <div class="g-build-detail-name">{meta.label}</div>
            <p class="g-build-detail-desc">Une piste part du haut d'une remontée ou d'une autre piste, et arrive à une gare, à une piste ou au village.</p>
          </aside>
        )}
      </div>
    </section>
  );
}

export function PerfPanel() {
  if (!ui.perf.value) return null;
  const s = ui.perfStats.value;
  const store = hud.store;
  const row = (k: string, v: string | number | undefined) => (
    <div class="kh-row" key={k}>
      <span class="muted">{k}</span>
      <span>{v ?? '-'}</span>
    </div>
  );
  const fmt = (v: unknown) => (typeof v === 'number' ? v.toLocaleString('fr-FR') : String(v ?? '-'));
  return (
    <HudPanel class="kh-perf" title="Performances" icon={Activity}>
      {row('Rendu', fmt(s.backend))}
      {row('Images/s', fmt(s.fps))}
      {row('Image (ms)', fmt(s.frameMs))}
      {row('CPU (ms) moy./max', `${fmt(s.cpuMs)} / ${fmt(s.cpuMaxMs)}`)}
      {row('Draw calls', fmt(s.drawCalls))}
      {row('Triangles', fmt(s.triangles))}
      {row('Terrain (tuiles / tris)', `${fmt(s.chunks)} / ${fmt(s.terrainTris)}`)}
      {row('Tuiles cachées par le relief', fmt(s.occluded))}
      {row('Skieurs dessinés / total', `${fmt(s.skiers)} / ${fmt(s.agents)}`)}
      {row('Décor (instances)', `${fmt(s.decor)} / ${fmt(s.trees)} sapins`)}
      {row('Village et pistes', `${fmt(s.houses)} chalets / ${fmt(s.poles)} jalons`)}
      {row('Sièges et cabines', fmt(s.carriers))}
      {row('Résolution / LOD', `${fmt(s.pixelRatio)} / ${fmt(s.lodScale)}`)}
      {store && row('Réseau', `${n0(store.rtt)} ms · ${n0(store.traffic.bytesPerSec / 1024)} Ko/s · ${store.traffic.msgsPerSec} msg/s`)}
      {store && row('Tick serveur', fmt(store.clock.tick))}
    </HudPanel>
  );
}
